import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import { fetchMyFeedback } from "../api/feedback";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { FeedbackDialog } from "../components/FeedbackDialog";
import { Skel } from "../components/Skeleton";
import { usePageMeta } from "../hooks/usePageMeta";

// The signed-in user's own submissions and whether we have answered. Replies
// are threaded on /feedback/:id; the bell and the reply email both link there.

const TYPE_LABEL: Record<string, string> = {
  bug: "Bug",
  feature: "Feature",
  data: "Missing data",
  other: "Other",
};

function formatDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" });
}

export default function FeedbackListPage() {
  usePageMeta({
    title: "Your feedback",
    description: "Everything you have sent Atlas, and our replies.",
    canonicalPath: "/feedback",
    noindex: true,
  });
  const [composing, setComposing] = React.useState(false);
  const query = useQuery({ queryKey: ["feedback", "mine"], queryFn: fetchMyFeedback });
  const items = query.data?.feedback ?? [];

  return (
    <div
      className="r-page"
      style={{ padding: 28, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 20 }}
    >
      <div className="r-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ font: "var(--type-display)", fontSize: 32, color: "var(--text-primary)", margin: "0 0 6px" }}>
            Your feedback
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0 }}>
            Everything you have sent us, and our replies.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setComposing(true)}>
          Send feedback
        </Button>
      </div>

      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          minWidth: 0,
        }}
      >
        {query.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={40} />
            <Skel height={40} />
          </div>
        ) : query.isError ? (
          <EmptyState
            icon={<MessageSquare size={20} />}
            title="Couldn't load your feedback"
            hint="Check your connection and try again."
            style={{ margin: "12px 0" }}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MessageSquare size={20} />}
            title="Nothing sent yet"
            hint="Bug reports, ideas and missing data requests you send will show up here with our replies."
            style={{ margin: "12px 0" }}
          />
        ) : (
          items.map((item) => (
            <Link
              key={item.id}
              to="/feedback/$id"
              params={{ id: item.id }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                padding: "12px 0",
                borderBottom: "1px solid var(--border-subtle)",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <Badge variant="neutral">{TYPE_LABEL[item.type] ?? item.type}</Badge>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", flex: 1, minWidth: 160 }}>
                {item.title}
              </span>
              {item.hasUnread ? (
                <Badge variant="info">New reply</Badge>
              ) : item.lastRepliedAt ? (
                <Badge variant="success">Replied</Badge>
              ) : null}
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatDate(item.createdAt)}</span>
            </Link>
          ))
        )}
      </div>

      {composing && <FeedbackDialog onClose={() => setComposing(false)} />}
    </div>
  );
}
